import { useMemo } from "react";
import type { Vec2 } from "./types";
import { boundingBox } from "./geom";

export interface FitInfo {
  /** Pixels per inch. */
  scale: number;
  /** Offset to add (in pixels) to room coords to center it in the canvas. */
  offsetX: number;
  offsetY: number;
  inToPx: (v: number) => number;
  pointToPx: (p: Vec2) => Vec2;
  pxToRoom: (px: Vec2) => Vec2;
}

export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const NO_INSETS: Insets = { top: 0, right: 0, bottom: 0, left: 0 };

/** Default blank-canvas sketching extent (inches): a 24'×18' room. */
const DEFAULT_BLANK_EXTENT_IN = { width: 24 * 12, height: 18 * 12 };
/** Breathing room kept past chrome that floats over the canvas, px — enough
 *  for the wall-length pills that straddle the outline. */
const CHROME_GAP_PX = 20;
/** Two opposite sides together may take at most this share of the canvas,
 *  so small canvases (the Walls view's floor plan, landscape phones) never
 *  collapse the room. */
const MAX_PAIR_SHARE = 0.6;

/**
 * Per-side padding for the fit: at least `basePx`, or clear of the chrome
 * covering that side plus {@link CHROME_GAP_PX}, whichever is larger. When
 * a pair of opposite sides would take more than {@link MAX_PAIR_SHARE} of
 * the canvas, both shrink proportionally.
 */
export function resolveFitPadding(
  basePx: number,
  insets: Insets,
  width: number,
  height: number
): Insets {
  const side = (inset: number) => Math.max(basePx, inset > 0 ? inset + CHROME_GAP_PX : 0);
  const pair = (a: number, b: number, dim: number): [number, number] => {
    const k = Math.min(1, (dim * MAX_PAIR_SHARE) / Math.max(a + b, 1e-6));
    return [a * k, b * k];
  };
  const [top, bottom] = pair(side(insets.top), side(insets.bottom), height);
  const [left, right] = pair(side(insets.left), side(insets.right), width);
  return { top, right, bottom, left };
}

/**
 * Compute a scale/offset that fits a polygon inside the canvas dims with
 * padding. `insets` is chrome floating over the canvas edges (top bar,
 * toolbars, floating buttons); the room is centered in what's left.
 */
export function useFitToRoom(
  points: Vec2[],
  width: number,
  height: number,
  paddingPx = 32,
  /** Extent (inches) of the centered area shown when `points` is empty (or
   *  the polygon is malformed). Defaults to a 24'×18' room; the furniture
   *  creator passes a smaller furniture-scale extent. */
  blankExtentIn: { width: number; height: number } = DEFAULT_BLANK_EXTENT_IN,
  insets: Insets = NO_INSETS
): FitInfo {
  const blankW = blankExtentIn.width;
  const blankH = blankExtentIn.height;
  const { top: insetT, right: insetR, bottom: insetB, left: insetL } = insets;
  return useMemo(() => {
    if (width === 0 || height === 0) {
      return {
        scale: 1,
        offsetX: 0,
        offsetY: 0,
        inToPx: (v) => v,
        pointToPx: (p) => p,
        pxToRoom: (p) => p,
      };
    }

    const pad = resolveFitPadding(
      paddingPx,
      { top: insetT, right: insetR, bottom: insetB, left: insetL },
      width,
      height
    );
    const availW = Math.max(1, width - pad.left - pad.right);
    const availH = Math.max(1, height - pad.top - pad.bottom);

    const build = (minX: number, minY: number, w: number, h: number): FitInfo => {
      const scale = Math.min(availW / Math.max(1, w), availH / Math.max(1, h));
      const offsetX = pad.left + (availW - w * scale) / 2 - minX * scale;
      const offsetY = pad.top + (availH - h * scale) / 2 - minY * scale;
      return {
        scale,
        offsetX,
        offsetY,
        inToPx: (v: number) => v * scale,
        pointToPx: (p: Vec2) => ({ x: p.x * scale + offsetX, y: p.y * scale + offsetY }),
        pxToRoom: (p: Vec2) => ({ x: (p.x - offsetX) / scale, y: (p.y - offsetY) / scale }),
      };
    };

    // Blank canvas: show a comfortable sketching area centered in the viewport.
    if (points.length === 0) return build(0, 0, blankW, blankH);

    const bb = boundingBox(points);
    // Guard against NaN/Infinity in the polygon — a single bad vertex
    // (e.g. from a malformed import or a buggy edit path) would otherwise
    // produce `scale: NaN`, which propagates into the canvas's grid
    // building loop in `RoomCanvas.tsx > buildGrid` and freezes the tab
    // (NaN comparisons short-circuit the loop's break condition). Falling
    // back to the empty-room defaults is far better than a crash.
    if (
      !Number.isFinite(bb.minX) ||
      !Number.isFinite(bb.minY) ||
      !Number.isFinite(bb.width) ||
      !Number.isFinite(bb.height)
    ) {
      return build(0, 0, blankW, blankH);
    }
    return build(bb.minX, bb.minY, bb.width, bb.height);
  }, [points, width, height, paddingPx, blankW, blankH, insetT, insetR, insetB, insetL]);
}
